import React, {FunctionComponent, useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Dialog} from '@blueprintjs/core';

import {loadRoles} from '../../../store/actions/roleActions';
import {IState} from '../../../store/reducers';
import {IRole, IUser, ApiStatus} from '../../../models';
import {inviteUser} from '../../../store/actions/userActions';
import InviteUserFormComponent from './InviteUserForm';
import {isAdmin, ROLE_ADMIN, ROLE_GUEST} from '../../../utils/auth';
import UserLimitContainer from '../../subscription/UserLimitContainer';
import {useCurrentUser} from "../../../hooks";

type Callback = (value?: any) => void;

type Defaults = { [key: string]: string };

interface IInviteUserDialogContainerProps {
  show: boolean;
  onClose: Callback;
  defaults?: Defaults;
}

interface IInviteUserDialogProps {
  show: boolean;
  roles: IRole[];
  defaults?: Defaults;
  onClose: Callback;
  onInvite: Callback;
  pending: boolean; // invitation
}

const InviteUserDialog: FunctionComponent<IInviteUserDialogProps> = ({
  show,
  roles,
  defaults,
  onClose,
  onInvite,
  pending,
}: IInviteUserDialogProps) => {
  return (
    <Dialog
      autoFocus={true}
      canEscapeKeyClose={true}
      canOutsideClickClose={true}
      enforceFocus={true}
      usePortal={true}
      isOpen={show}
      onClose={onClose}
      title="Invite new Users"
      icon="user"
    >
      <InviteUserFormComponent
        roles={roles}
        defaults={defaults}
        onClose={onClose}
        onInvite={onInvite}
        pending={pending}/>
    </Dialog>
  );
};

const InviteUserDialogContainer_: FunctionComponent<IInviteUserDialogContainerProps> = ({
  show = false,
  onClose,
  defaults,
}: IInviteUserDialogContainerProps) => {
  const [availableRoles, setAvailableRoles] = useState<IRole[]>([]);
  const roles = useSelector((state: IState) => state.roles.roles);
  const invitingStatus = useSelector((state: IState) => state.users.invitingStatus);
  const currentUser = useCurrentUser();
  const usersOverLimit = false; // useUsersOverLimit(); // TODO: revert after subscription limit is implemented
  const dispatch = useDispatch();

  useEffect(() => {
    if (!roles || (roles && !roles.length)) {
      dispatch(loadRoles());
    } else if (roles?.length && !availableRoles.length && currentUser) {
      if (usersOverLimit) {
        /*
         * If user usage is over limit, set only available option as ROLE_GUEST
         */
        setAvailableRoles(
          roles.filter((role: IRole) => role.name.toLowerCase() === ROLE_GUEST)
        );
      } else if (!isAdmin(currentUser)) {
        // filter out admin role
        setAvailableRoles(
          roles.filter((role: IRole) => role.name.toLowerCase() !== ROLE_ADMIN)
        );
      } else {
        setAvailableRoles(roles);
      }
    }
  }, [roles, currentUser, usersOverLimit]);

  const onUserInvite = useCallback((user: Partial<IUser>) => {
    dispatch(inviteUser(user));
  }, []);

  return (
    <InviteUserDialog
      show={show}
      onClose={onClose}
      roles={availableRoles}
      defaults={defaults}
      pending={invitingStatus === ApiStatus.LOADING}
      onInvite={onUserInvite}/>
  )
};

const InviteUserDialogContainer = (props: any) => {
  /*
   * InviteUserDialogContainer HOC
   */
  return (
    <UserLimitContainer>
      <InviteUserDialogContainer_ {...props} />
    </UserLimitContainer>
  );
}

export default InviteUserDialogContainer;
